
import { S3_BUCKET, S3_REGION } from '@/api';
import { WeddingApi } from '@/api/WeddingApi';
import { GENERIC_ERROR_DESCRIPTION } from '@/constants';
import { Styles, WeddingEvent, WeddingEventImage, WeddingEventImageLike } from 'ignite360-core';
import { Component, Vue, Watch } from 'vue-property-decorator';
import UploadPublicWeddingEventImages from '@/components/bot/wedding/UploadPublicWeddingEventImages.vue';
import WeddingEventImageModal from '@/components/bot/wedding/WeddingEventImageModal.vue';
import { Route } from 'vue-router';
import ImageView from '@/components/bot/wedding/ImageView.vue';
import { WeddingEventImageCaptionSettings, WeddingEventImageCommentSettings } from '@/types';
import { isMobile } from '@/utilities';

// must match the sizes the upload component produces (UploadPublicWeddingEventImages.vue)
const THUMBNAIL_SIZE = 400;
const MAX_IMAGE_SIZE = 1000;
@Component({
  name: 'public-wedding-event',
  components: { UploadPublicWeddingEventImages, WeddingEventImageModal, ImageView },
})
export default class PublicWeddingEvent extends Vue {
  isLoading = false;
  event: WeddingEvent | null = null;
  showImageModal = false;
  currentImageIndex = -1;

  userSessionId: string | null = null;
  deletingIds: string[] = [];

  get sortedImages() {
    const allImages = [...(this.event?.images || [])];
    return allImages.sort((a, b) => a.order - b.order);
  }

  get isGridView() {
    return this.currentImageIndex === -1;
  }

  get heroImage() {
    return this.event?.images.filter((image) => image.heroImage)[0] || undefined;
  }

  get heroImageIndex() {
    return this.sortedImages.findIndex((image) => image.heroImage);
  }

  get currentImage() {
    return this.currentImageIndex >= 0 ? this.sortedImages[this.currentImageIndex] : null;
  }

  get isMobile() {
    return isMobile();
  }

  get isCelebration(): boolean {
    return this.event?.template === 'celebration';
  }

  get showUpload(): boolean {
    return !this.isLoading && !!this.event?.configAllowImageUploads;
  }

  // opens the file picker of the upload card (also from the sticky button / "add more" card)
  openFilePicker() {
    (this.$refs.uploader as UploadPublicWeddingEventImages | undefined)?.openFilePicker();
  }

  isVideo(image: WeddingEventImage) {
    return (
      image.extension === 'mp4' ||
      image.extension === 'mov' ||
      image.extension === 'avi' ||
      image.extension === 'webm'
    );
  }
  canDeleteImage(image: WeddingEventImage) {
    return image.state === 'in_review' || image.userSessionId === this.userSessionId;
  }

  goToGallery() {
    // stop videos
    const videos = document.querySelectorAll('video');
    videos.forEach((video) => {
      video.pause();
    });
    this.currentImageIndex = -1;
    this.$router.push({
      path: this.$route.path,
      query: {},
    });
  }

  openImage(index: number) {
    if (isMobile()) {
      this.currentImageIndex = index;
      this.$router.push({
        path: this.$route.path,
        query: { imageId: this.sortedImages[index]?.id },
      });
      // Add scroll behavior
      this.$nextTick(() => {
        const imageElement = document.getElementById(`image-${this.sortedImages[index]?.id}`);
        if (imageElement) {
          imageElement.scrollIntoView({ behavior: 'auto', block: 'start' });
        }
      });
    } else {
      this.handleOpenImageModal(index);
    }
  }

  handleOpenImageModal(index: number) {
    this.currentImageIndex = index;
    this.showImageModal = true;

    const imageId = this.sortedImages[index]?.id;
    if (imageId) {
      const query = { ...this.$route.query };
      query.imageId = imageId;

      this.$router.push({
        path: this.$route.path,
        query,
      });
    }
  }

  getStyle(key: keyof Styles) {
    return this.event?.styles[key];
  }

  get eventId(): string {
    return this.$route.params.id || this.$route.params.slug || '';
  }

  get botId(): string {
    return this.event?.bot.id || '';
  }

  // The heart updates immediately. Requests per image run one after another and only send
  // the latest state, so fast clicking can't create duplicate likes or jump back.
  toggleLike(image: WeddingEventImage) {
    const userSessionId = localStorage.getItem('userSessionId')!;
    if (this.isLiked(image)) {
      image.likes = (image.likes || []).filter((like) => like.userSessionId !== userSessionId);
    } else {
      image.likes = [...(image.likes || []), { userSessionId } as WeddingEventImageLike];
    }
    this.syncLike(image, userSessionId);
  }

  likeSyncing: Record<string, boolean> = {};

  async syncLike(image: WeddingEventImage, userSessionId: string) {
    if (this.likeSyncing[image.id]) return; // the running sync picks up the latest state
    this.likeSyncing[image.id] = true;
    try {
      let sent: boolean;
      do {
        sent = this.isLiked(image);
        await WeddingApi.setLike(this.event!.id, image.id, userSessionId, sent);
      } while (sent !== this.isLiked(image));
    } catch (error) {
      console.error('Error saving like:', error);
      await this.loadEvent(true); // show what is actually saved
    } finally {
      this.likeSyncing[image.id] = false;
    }
  }

  async loadEvent(reload = false) {
    if (!reload) {
      this.isLoading = true;
    }
    try {
      const [eventData] = await Promise.all([
        WeddingApi.getPublicWeddingEvent(this.eventId, localStorage.getItem('userSessionId')!),
        new Promise((resolve) => setTimeout(resolve, 500)),
      ]);
      this.event = eventData;
      console.log('event', this.event);

      if (reload) {
        return;
      }

      const imageId = this.$route.query.imageId;
      if (imageId && this.event) {
        this.$nextTick(() => {
          const index = this.sortedImages.findIndex((img) => img.id === imageId);
          if (index !== -1) {
            this.openImage(index);
          }
        });
      }
      // upload document title
      document.title = this.event.title;
    } catch (e) {
      this.$notify.error({
        title: 'Loading project failed',
        description: GENERIC_ERROR_DESCRIPTION,
      });
    } finally {
      this.isLoading = false;
    }
  }

  async created() {
    // create user session id and save it to local storage
    let userSessionId = localStorage.getItem('userSessionId');
    if (!userSessionId) {
      userSessionId = Math.random().toString(36).substring(2, 15);
      localStorage.setItem('userSessionId', userSessionId);
    }
    this.userSessionId = userSessionId;
    await this.loadEvent();
  }

  getEventTitleSplit() {
    return this.event?.title?.split(' ') || [];
  }

  getEventDescriptionSplit() {
    return this.event?.description?.split('\n') || [];
  }

  getNames() {
    return ['Bree', 'Nick', 'Cooper', 'and Levi'];
  }

  imageUrl(image: WeddingEventImage) {
    return `https://${S3_BUCKET}.s3.${S3_REGION}.amazonaws.com/bots/${
      this.event!.bot.id
    }/wedding/events/${this.event!.id}/images/${image.id}.${image.extension}`;
  }

  // small version for the grid, older images have none (see BotWeddingService.thumbnailS3Key)
  gridImageUrl(image: WeddingEventImage) {
    if (!image.hasThumbnail) {
      return this.imageUrl(image);
    }
    return `https://${S3_BUCKET}.s3.${S3_REGION}.amazonaws.com/bots/${
      this.event!.bot.id
    }/wedding/events/${this.event!.id}/images/${image.id}_thumb.jpg`;
  }

  // the browser picks the thumbnail on phones and the full image for the bigger tiles
  // on desktop, where the 400px thumbnail would look blurry
  gridSrcset(image: WeddingEventImage) {
    if (!image.hasThumbnail) {
      return undefined;
    }
    return `${this.gridImageUrl(image)} ${THUMBNAIL_SIZE}w, ${this.imageUrl(
      image,
    )} ${MAX_IMAGE_SIZE}w`;
  }

  handleFileUploaded(images: WeddingEventImage[]) {
    this.loadEvent(true);
  }

  async deleteImage(image: WeddingEventImage) {
    if (this.deletingIds.includes(image.id)) return;
    this.deletingIds.push(image.id);
    try {
      await WeddingApi.deletePublicBotWeddingEventImage(
        this.botId,
        this.event!.id,
        image.id,
        this.userSessionId!,
      );
      // remove locally instead of reloading the whole event
      this.event!.images = this.event!.images.filter((img) => img.id !== image.id);
    } catch (e) {
      this.$notify.error({
        title: 'Deleting image failed',
        description: GENERIC_ERROR_DESCRIPTION,
      });
    } finally {
      this.deletingIds = this.deletingIds.filter((id) => id !== image.id);
    }
  }

  handleCurrentImageUpdate(newImage: WeddingEventImage): void {
    this.currentImageIndex = this.sortedImages.findIndex((img) => img.id === newImage.id);

    // Update URL
    const query = { ...this.$route.query };
    query.imageId = newImage.id;
    this.$router.replace({
      path: this.$route.path,
      query,
    });
  }

  handleCloseModal() {
    this.showImageModal = false;
    const query = { ...this.$route.query };
    delete query.imageId;
    this.$router.push({
      path: this.$route.path,
      query,
    });
  }

  @Watch('$route')
  onRouteChange(to: Route, from: Route) {
    const imageId = to.query.imageId;
    if (!imageId) {
      this.showImageModal = false;
      this.currentImageIndex = -1;
    } else if (this.event) {
      const index = this.sortedImages.findIndex((img) => img.id === imageId);
      if (index !== -1) {
        if (this.isMobile) {
          this.currentImageIndex = index;
          this.$nextTick(() => {
            const imageElement = document.getElementById(`image-${imageId}`);
            if (imageElement) {
              imageElement.scrollIntoView({ behavior: 'auto', block: 'center' });
            }
          });
        } else {
          this.currentImageIndex = index;
          this.showImageModal = true;
        }
      }
    }
  }

  async deleteComment(commentId: string) {
    await WeddingApi.deletePublicComment(
      this.event!.id,
      this.currentImage!.id,
      commentId,
      this.userSessionId!,
    );
    await this.loadEvent(true);
  }

  async submitComment(commentForm: WeddingEventImageCommentSettings): Promise<void> {
    if (!this.currentImage) return;
    try {
      await WeddingApi.createPublicImageComment(
        this.event!.id,
        this.currentImage.id,
        commentForm,
        localStorage.getItem('userSessionId')!,
      );

      // add name to local storage
      localStorage.setItem('wedding-event-image-comment-name', commentForm.name);
      await this.loadEvent();
    } catch (error) {
      console.error('Error submitting comment:', error);
    }
  }

  async submitCaption(captionForm: WeddingEventImageCaptionSettings): Promise<void> {
    if (!this.currentImage) return;
    try {
      await WeddingApi.updatePublicImageCaption(
        this.event!.id,
        this.currentImage.id,
        { caption: captionForm.title! },
        localStorage.getItem('userSessionId')!,
      );
      await this.loadEvent(true);
    } catch (error) {
      console.error('Error submitting caption:', error);
    }
  }

  isLiked(image: WeddingEventImage): boolean {
    return !!image.likes?.some((like) => like.userSessionId === this.userSessionId);
  }
}
